/**
 * Per-line claim state pill for SplitPay — used in Phase 6.
 * Variants: "unclaimed" | "you" | "other" | "split" (e.g. 1/3).
 */
import type { GroupCartParticipant } from "@/api/group-cart";
import { colorFor } from "@/lib/participant-colors";
import { initialFor } from "./Avatar";

export type ClaimPillKind = "unclaimed" | "you" | "other" | "split" | "paid" | "pending_cash";

export interface ClaimPillProps {
  kind: ClaimPillKind;
  /** Required when kind === "other". */
  payer?: GroupCartParticipant;
  /** Numerator/denominator for kind === "split". */
  splitN?: number;
  splitDenom?: number;
  /** Optional custom text. */
  label?: string;
}

export function ClaimPill({ kind, payer, splitN, splitDenom, label }: ClaimPillProps) {
  if (kind === "unclaimed") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 10px",
          borderRadius: "var(--radius-chip)",
          background: "var(--surface-2)",
          color: "var(--ink-3)",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        Unclaimed
      </span>
    );
  }
  if (kind === "you") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 10px 5px 8px",
          borderRadius: "var(--radius-chip)",
          background: "var(--primary-600)",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12l4.5 4.5L19 7" />
        </svg>
        You
      </span>
    );
  }
  if (kind === "other" && payer) {
    const color = colorFor(payer.colorIndex);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 10px 3px 3px",
          borderRadius: "var(--radius-chip)",
          background: color.soft,
          color: color.fg,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            background: color.strong,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          {initialFor(payer)}
        </span>
        {payer.displayName}
      </span>
    );
  }
  if (kind === "split" && splitN && splitDenom) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 10px",
          borderRadius: "var(--radius-chip)",
          background: "#efe1ff",
          color: "#6730c4",
          fontSize: 11,
          fontWeight: 700,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {splitN}/{splitDenom}
      </span>
    );
  }
  if (kind === "paid") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 10px",
          borderRadius: "var(--radius-chip)",
          background: "#dcfce7",
          color: "#166534",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {label || "Paid"}
      </span>
    );
  }
  if (kind === "pending_cash") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 10px",
          borderRadius: "var(--radius-chip)",
          background: "#fef9c3",
          color: "#854d0e",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {label || "Pending Cash"}
      </span>
    );
  }
  return null;
}
